import Axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { api_url } from "../../../config";
import Bouton from "../../common/HorsConnexion/Bouton";
import Compte from "../../common/HorsConnexion/Compte";
import EcranResultat from "../../common/HorsConnexion/EcranResultat";
import {
  MESSAGE_SERVEUR_INDISPONIBLE,
  extraireErreurMotDePasse,
  interpreterErreurLien,
} from "../../common/HorsConnexion/horsConnexion";
import MessageEtat from "../../common/HorsConnexion/MessageEtat";
import PageHorsConnexion from "../../common/HorsConnexion/PageHorsConnexion";
import PiedCarte from "../../common/HorsConnexion/PiedCarte";
import ReglesMotDePasse, { evaluerMotDePasse } from "../../common/HorsConnexion/ReglesMotDePasse";
import Saisie from "../../common/HorsConnexion/Saisie";

/**
 * Nouveau mot de passe depuis le lien reçu par e-mail (maquette 07). Le lien
 * est vérifié dès l'ouverture (GET auth/renew-password/token/:token) : le
 * formulaire n'apparaît que s'il est utilisable, sinon l'écran expiré / déjà
 * utilisé / invalide s'affiche sans rien demander. L'API le revérifie de toute
 * façon à l'enregistrement, d'où les mêmes écrans après la soumission.
 */
const RenewPassword = (props) => {
  const { token, email } = props.match.params;
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [erreurMdp, setErreurMdp] = useState(null); // renvoyée par l'API sur le mot de passe
  const [erreurGenerale, setErreurGenerale] = useState(null);
  const [enregistrement, setEnregistrement] = useState(false);
  const [vue, setVue] = useState(token && email ? "chargement" : "invalide");

  const verifierLien = useCallback(() => {
    let demonte = false;
    setVue("chargement");
    Axios.get(api_url + "auth/renew-password/token/" + encodeURIComponent(token))
      .then(() => {
        if (!demonte) setVue("formulaire");
      })
      .catch((err) => {
        if (!demonte) setVue(interpreterErreurLien(err));
      });
    return () => {
      demonte = true;
    };
  }, [token]);

  useEffect(() => {
    if (!token || !email) return undefined;
    return verifierLien();
  }, [token, email, verifierLien]);

  const { conforme } = evaluerMotDePasse(password);
  const identiques = confirm.length > 0 && confirm === password;
  const peutEnregistrer = conforme && identiques && !enregistrement && !erreurMdp;

  function enregistrer(e) {
    e.preventDefault();
    if (!peutEnregistrer) return;
    setEnregistrement(true);
    setErreurGenerale(null);
    Axios.post(api_url + "managers/renew-password/", { token, email, password })
      .then(() => {
        setVue("succes");
      })
      .catch((err) => {
        const donnees = err.response && err.response.data;
        const messageMdp = extraireErreurMotDePasse(donnees);
        if (messageMdp) {
          setErreurMdp(messageMdp);
          return;
        }
        const resultat = interpreterErreurLien(err);
        if (resultat === "indisponible") setErreurGenerale(MESSAGE_SERVEUR_INDISPONIBLE);
        else setVue(resultat);
      })
      .then(() => setEnregistrement(false));
  }

  const versConnexion = { pathname: "/", state: { email } };
  const versNouveauLien = { pathname: "/", state: { vue: "oubli", email } };
  const boutonsNouveauLien = (
    <>
      <Bouton to={versNouveauLien}>Demander un nouveau lien</Bouton>
      <Bouton secondaire to="/">
        Retour à la connexion
      </Bouton>
    </>
  );

  return (
    <PageHorsConnexion>
      {vue === "chargement" && (
        <EcranResultat ton="attente" titre="Vérification de votre lien…">
          <p>Un instant, nous vérifions que ce lien est toujours valable.</p>
          <Compte email={email} />
        </EcranResultat>
      )}
      {vue === "formulaire" && (
        <section aria-labelledby="titre-nouveau-mdp">
          <h2 id="titre-nouveau-mdp">Nouveau mot de passe</h2>
          <p className="sous-titre">
            Choisissez le mot de passe du compte ci-dessous. Il remplacera l'ancien dès
            l'enregistrement.
          </p>
          <Compte email={email} />

          <form onSubmit={enregistrer} noValidate>
            <Saisie
              id="mdp"
              label="Nouveau mot de passe"
              revelable
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setErreurMdp(null);
              }}
              erreur={erreurMdp}
              aide={<ReglesMotDePasse motDePasse={password} />}
              autoComplete="new-password"
              placeholder="9 caractères minimum"
              autoFocus
            />
            <Saisie
              id="confirm"
              label="Confirmez le mot de passe"
              revelable
              libelleRevelation="la confirmation"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              erreur={confirm.length > 0 && !identiques ? "Les deux mots de passe ne sont pas identiques." : null}
              ok={identiques && conforme && !erreurMdp ? "Les deux mots de passe correspondent." : null}
              autoComplete="new-password"
              placeholder="Saisissez-le une seconde fois"
            />

            {erreurGenerale && (
              <MessageEtat ton="danger" icone="alerte">
                {erreurGenerale}
              </MessageEtat>
            )}

            <Bouton type="submit" charge={enregistrement} disabled={!peutEnregistrer}>
              {enregistrement ? "Enregistrement…" : "Enregistrer le mot de passe"}
            </Bouton>
          </form>

          <PiedCarte signature={false}>Ce lien est valable 24 heures et ne sert qu'une fois.</PiedCarte>
        </section>
      )}
      {vue === "succes" && (
        <EcranResultat ton="succes" icone="coche" titre="Mot de passe enregistré">
          <p>Votre nouveau mot de passe est actif. Connectez-vous dès maintenant avec celui-ci.</p>
          <Compte email={email} />
          <Bouton to={versConnexion}>Se connecter</Bouton>
        </EcranResultat>
      )}
      {vue === "expire" && (
        <EcranResultat ton="avert" icone="horloge" titre="Ce lien a expiré">
          <p>
            Les liens de réinitialisation sont valables <strong>24 heures</strong>. Celui-ci n'est
            plus utilisable.
          </p>
          {boutonsNouveauLien}
        </EcranResultat>
      )}
      {vue === "utilise" && (
        <EcranResultat ton="info" icone="info" titre="Ce lien a déjà été utilisé">
          <p>
            Un lien de réinitialisation ne sert qu'une fois. Si vous n'êtes pas à l'origine de ce
            changement, demandez un nouveau lien sans attendre.
          </p>
          {boutonsNouveauLien}
        </EcranResultat>
      )}
      {vue === "invalide" && (
        <EcranResultat ton="danger" icone="croix" titre="Ce lien est invalide">
          <p>
            Il est peut-être tronqué ou mal recopié. Ouvrez-le directement depuis l'e-mail que vous
            avez reçu.
          </p>
          {boutonsNouveauLien}
        </EcranResultat>
      )}
      {vue === "indisponible" && (
        <EcranResultat ton="danger" icone="alerte" titre="Vérification impossible pour le moment">
          <p>Le serveur n'a pas répondu. Réessayez dans quelques instants.</p>
          <Bouton onClick={verifierLien}>Réessayer</Bouton>
          <Bouton secondaire to="/">
            Retour à la connexion
          </Bouton>
        </EcranResultat>
      )}
    </PageHorsConnexion>
  );
};

export default RenewPassword;
